import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PromoBanners.css';
import { useCategories } from '../../hooks/useCategories';
import { getCategoryImageUrl } from '../../utils/helpers';
import { ArrowRight, Sparkles, Tag, ShoppingBag } from 'lucide-react';

// Import fallback images
import heropic from '../Assets/heropic.jpg';
import deoimage from '../Assets/deoimage.jpg';

// Helper function to shuffle array randomly
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const PromoBanners = () => {
  const { t } = useTranslation('common');
  // Fetch categories for promotional banners (focus on categories, not products)
  const { data: categories = [], isLoading: isLoadingCategories } = useCategories();

  // Create full-width banner from top category
  const fullBanner = useMemo(() => {
    if (categories.length === 0) {
      return {
        id: 'fallback-full',
        image: heropic,
        title: t('discoverOurCollections', { defaultValue: 'Discover Our Collections' }),
        subtitle: t('shopByCategory', { defaultValue: 'Shop by category and find your perfect style' }),
        link: "/search",
        buttonText: t('exploreAll', { defaultValue: 'Explore All' })
      };
    }
    
    // Select random top category
    const shuffledCategories = shuffleArray(categories);
    const category = shuffledCategories[0];
    const categoryId = category._id || category.id;
    const categoryImage = getCategoryImageUrl(category, heropic);
    
    return {
      id: `category-full-${categoryId}`,
      image: categoryImage,
      title: `${t('shop', { defaultValue: 'Shop' })} ${category.name}`,
      subtitle: `${t('discoverOur', { defaultValue: 'Discover our' })} ${category.name} ${t('collection', { defaultValue: 'collection' })}`,
      link: `/category/${categoryId}`,
      buttonText: t('exploreCollection', { defaultValue: 'Explore Collection' })
    };
  }, [categories, t]);

  // Create side banners from categories (not products)
  const sideBanners = useMemo(() => {
    const banners = [];
    
    // Use categories for side banners (up to 2)
    if (categories.length > 0) {
      const shuffledCategories = shuffleArray(categories);
      const selectedCategories = shuffledCategories.slice(0, 2);
      
      selectedCategories.forEach((category, index) => {
        const categoryId = category._id || category.id;
        const categoryImage = getCategoryImageUrl(category, deoimage);
        
        banners.push({
          id: `category-${categoryId || index}`,
          image: categoryImage,
          title: category.name,
          link: `/category/${categoryId}`,
          buttonText: t('shopNow'),
          type: 'category'
        });
      });
    }
    
    // If we have less than 2 categories, add a SALE banner
    if (banners.length < 2) {
      banners.push({
        id: 'sale-banner',
        type: 'sale',
        title: t('sale', { defaultValue: 'SALE' }),
        link: "/search",
        buttonText: t('shopNow')
      });
    }
    
    // Fallback if no data
    if (banners.length === 0) {
      return [
        {
          id: 'fallback-sale',
          type: 'sale',
          title: t('sale', { defaultValue: 'SALE' }),
          link: "/search",
          buttonText: t('shopNow')
        },
        {
          id: 'fallback-category',
          image: deoimage,
          title: t('newCollection', { defaultValue: 'New Collection' }),
          link: "/search",
          buttonText: t('explore', { defaultValue: 'Explore' }),
          type: 'category'
        }
      ];
    }
    
    return banners;
  }, [categories, t]);

  if (isLoadingCategories && categories.length === 0) {
    return (
      <section className="promo-section">
        <div className="promo-container">
          <div className="promo-loading">
            <div className="loading-skeleton promo-banner-full" />
            <div className="loading-skeleton promo-grid">
              <div className="loading-skeleton promo-banner" />
              <div className="loading-skeleton promo-banner" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="promo-section">
      <div className="promo-container">
        {/* Full Width Banner - Category Focused */}
        {fullBanner && (
          <Link 
            to={fullBanner.link} 
            className="promo-banner-full"
            style={{ 
              backgroundImage: `url(${fullBanner.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="promo-overlay">
              <div className="promo-content">
                <div className="promo-category-badge">
                  <ShoppingBag size={20} />
                  <span>{t('collection', { defaultValue: 'Collection' })}</span>
                </div>
                <h3>{fullBanner.title}</h3>
                <p>{fullBanner.subtitle}</p>
                <span className="promo-btn">
                  {fullBanner.buttonText}
                  <ArrowRight size={18} />
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Two Column Banners - Categories & Sales */}
        <div className="promo-grid">
          {sideBanners.map((banner, index) => {
            if (banner.type === 'sale') {
              return (
                <Link 
                  key={banner.id} 
                  to={banner.link} 
                  className="promo-banner sale-banner"
                >
                  <div className="sale-content">
                    <Sparkles size={48} className="sale-icon" />
                    <span className="sale-text">{t('sale', { defaultValue: 'SALE' })}</span>
                    <span className="sale-subtitle">{t('upTo50Off', { defaultValue: 'Up to 50% Off' })}</span>
                    <div className="sale-tags">
                      <Tag size={16} />
                      <span>{t('limitedTime', { defaultValue: 'Limited Time', ns: 'hero' })}</span>
                    </div>
                  </div>
                </Link>
              );
            }
            
            return (
              <Link 
                key={banner.id}
                to={banner.link} 
                className="promo-banner category-banner"
                style={{ 
                  backgroundImage: `url(${banner.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="promo-overlay-dark">
                  <div className="category-badge">
                    <ShoppingBag size={18} />
                  </div>
                  <h4>{banner.title}</h4>
                  <span className="promo-btn-small">
                    {banner.buttonText}
                    <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PromoBanners;
